import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { Github, Linkedin, Mail, ArrowUpRight, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import shellyImg from './headshot_of_me.png';
import hikingImg from './hiking_photo.JPG';
import hikingImg2 from './IMG_2165.jpg';
import IntroScreen from './IntroScreen';

// ─── Scroll-reveal wrapper ─────────────────────────────────────────────────────
const Reveal = ({ children, className = '', delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });

  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y: 28 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.75, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
};

// ─── Card ──────────────────────────────────────────────────────────────────────
const Card = ({ children, className = '' }) => (
  <motion.div
    className={`rounded-lg p-5 border bg-white/[0.02] border-white/[0.06] ${className}`}
    whileHover={{ y: -3, backgroundColor: 'rgba(255,255,255,0.035)', borderColor: 'rgba(255,255,255,0.11)' }}
    transition={{ type: 'spring', stiffness: 320, damping: 22 }}
  >
    {children}
  </motion.div>
);

// ─── Tag ──────────────────────────────────────────────────────────────────────
const Tag = ({ children }) => (
  <span className="text-[10px] font-mono px-2.5 py-0.5 rounded-sm border border-white/[0.06] text-white/28 bg-white/[0.018] tracking-wide">
    {children}
  </span>
);

// ─── Image Carousel ────────────────────────────────────────────────────────────
const ImageCarousel = ({ images, alt }) => {
  const [current, setCurrent] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setCurrent((p) => (p + 1) % images.length), 3000);
    return () => clearInterval(t);
  }, [images.length]);

  return (
    <div className="mb-5 rounded-md overflow-hidden border border-white/[0.05] relative aspect-video bg-white/[0.025]">
      <img
        src={images[current]}
        alt={`${alt} ${current + 1}`}
        className="w-full h-full object-cover transition-opacity duration-500"
      />
      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
        {images.map((_, i) => (
          <button
            key={i}
            onClick={() => setCurrent(i)}
            className={`h-0.5 rounded-full transition-all duration-300 ${
              i === current ? 'w-5 bg-teal-400' : 'w-1.5 bg-white/20'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

// ─── About photos ──────────────────────────────────────────────────────────────
const photos = [
  { src: shellyImg, name: 'me.png', note: 'main()' },
  { src: hikingImg, name: 'trail_01.jpg', note: 'out of scope' },
  { src: hikingImg2, name: 'trail_02.jpg', note: 'every trail is a decision tree' },
];

// Sliding carousel: arrows on hover, dots below, auto-advance paused on hover.
const PhotoCarousel = () => {
  const n = photos.length;
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setI((p) => (p + 1) % n), 4500);
    return () => clearInterval(t);
  }, [paused, n]);

  const go = (d) => setI((p) => (p + d + n) % n);
  const arrow =
    'absolute top-1/2 -translate-y-1/2 z-10 p-1.5 bg-black/45 backdrop-blur-sm border border-white/10 ' +
    'text-white/60 hover:text-teal-400 hover:border-teal-400/40 opacity-0 group-hover:opacity-100 ' +
    'transition-all duration-200';

  return (
    <div
      className="w-full max-w-[300px]"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div
        className="group relative overflow-hidden border border-white/[0.07] bg-white/[0.02]"
        style={{ borderRadius: '4px', aspectRatio: '4/5' }}
      >
        <motion.div
          className="flex h-full"
          animate={{ x: `-${i * 100}%` }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
          {photos.map((p) => (
            <div key={p.name} className="relative w-full h-full flex-shrink-0">
              <img
                src={p.src}
                alt={p.note}
                draggable={false}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-x-0 bottom-0 p-3 pt-9 bg-gradient-to-t from-black/80 to-transparent">
                <p
                  className="text-[9.5px] text-white/60 leading-snug"
                  style={{ fontFamily: "'JetBrains Mono', monospace" }}
                >
                  <span className="text-teal-400/50">{'// '}</span>
                  {p.note}
                </p>
              </div>
            </div>
          ))}
        </motion.div>

        <button
          onClick={() => go(-1)}
          aria-label="Previous photo"
          className={`${arrow} left-2`}
          style={{ borderRadius: '2px' }}
        >
          <ChevronLeft size={13} />
        </button>
        <button
          onClick={() => go(1)}
          aria-label="Next photo"
          className={`${arrow} right-2`}
          style={{ borderRadius: '2px' }}
        >
          <ChevronRight size={13} />
        </button>
      </div>

      <div className="flex justify-center items-center gap-1.5 mt-3">
        {photos.map((p, k) => (
          <button
            key={p.name}
            onClick={() => setI(k)}
            aria-label={`Show ${p.name}`}
            className={`h-0.5 rounded-full transition-all duration-300 ${
              k === i ? 'w-5 bg-teal-400' : 'w-1.5 bg-white/20 hover:bg-white/40'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

// ─── Data ─────────────────────────────────────────────────────────────────────
const skills = [
  'React', 'Node.js', 'Python', 'JavaScript', 'TypeScript',
  'C', 'Java', 'SQL', 'Firebase', 'REST APIs', 'Tailwind CSS',
  'Machine Learning', 'Data Analysis', 'UI/UX', 'Git',
];

const experiences = [
  {
    role: 'Shopify Solutions Engineer',
    company: 'Helios Technology Solutions Inc',
    period: 'Internship · May 2026 – Aug 2026',
    location: 'Ontario, CA',
    points: [
      'Migrated legacy customer data into Shopify ensuring seamless integration with existing e-commerce workflows',
      'Validated data integrity throughout migration, preventing data loss and maintaining accuracy',
      'Collaborated with team to troubleshoot migration issues and optimize data transfer processes',
    ],
    tags: ['Data Migration', 'Shopify', 'Data Integrity', 'E-commerce'],
  },
  {
    role: 'Tech Coach',
    company: 'Vaughan Public Libraries',
    period: 'Jan 2022 – Jan 2024',
    location: 'Ontario, CA',
    points: [
      'Taught C programming fundamentals, automation, and problem-solving to diverse student groups',
      'Built a reminder notification app with API integration, database management, and mobile-first design',
      'Developed curriculum and hands-on exercises to enhance student engagement',
    ],
    tags: ['C Programming', 'API Integration', 'Database Management', 'Teaching'],
  },
  {
    role: 'Retail Sales Associate',
    company: 'Nike',
    period: '',
    location: '',
    points: [
      "Used CRM tools and data insights to support sales for Nike's premium product lines",
      'Strengthened customer relationships through targeted retail and outreach efforts',
    ],
    tags: ['CRM Systems', 'Data Analytics', 'Customer Relations'],
  },
];

const projects = [
  {
    title: 'Neural Code Simulator',
    desc: 'Generates synthetic spike data and uses a custom Random Forest model to classify neural activity as normal, Parkinsonian, or epileptiform in real time.',
    tags: ['Python', 'ML', 'Simulation'],
    link: 'https://neuraltool.streamlit.app/',
    images: ['/images/neural-simulator-1.png', '/images/neural-simulator-2.png'],
  },
  {
    title: 'Shape Shifting Chess',
    desc: 'A chess game where pieces dynamically change form and abilities as the match progresses, creating constantly evolving strategy.',
    tags: ['HTML', 'Game Dev', 'UI/UX'],
    link: 'https://shapeshifterchess.netlify.app/',
    images: ['/images/chess-game-1.png', '/images/chess-game-2.png'],
  },
  {
    title: 'ClubConnect',
    desc: 'A student incubator platform matching students with professors, supporting full project collaboration through real-time tools and a modern dashboard.',
    tags: ['React', 'Node.js', 'Database'],
    link: 'https://club-connect-demo.vercel.app/',
    images: ['/images/clubconnect-1.png', '/images/clubconnect-2.png'],
  },
  {
    title: 'Travel Pal',
    desc: 'A smart travel planner generating personalized, eco-friendly itineraries using AI, real-time data, and a clean responsive dashboard.',
    tags: ['React', 'API Integration', 'AI'],
    link: 'https://travelpal-taupe.vercel.app/',
    images: ['/images/travel-pal-1.png', '/images/travel-pal-2.png'],
  },
];

const involvement = [
  {
    title: 'VP — SOCIS',
    subtitle: 'Society of Computer and Information Sciences',
    points: [
      'Leading initiatives to foster community among CS students at U of G',
      'Organizing events, workshops, and networking opportunities',
      'Collaborating with faculty to improve CS program engagement',
    ],
    tags: ['Leadership', 'Event Planning', 'Community'],
  },
  {
    title: 'Member — GDSC',
    subtitle: 'Google Developer Student Club',
    points: [
      "Engaging with Google's developer community and cutting-edge tech",
      'Collaborated in hackathon to build innovative solutions',
      'Workshops on Google Cloud, Firebase, and Android',
    ],
    tags: ['Google Cloud', 'Firebase', 'Android'],
  },
  {
    title: 'AI Ambassador',
    subtitle: 'Thryve AI · University of Guelph',
    points: [
      'Representing university AI initiatives and promoting awareness',
      'Bridging academic AI research and practical implementation',
      'Mentoring students in AI and machine learning projects',
    ],
    tags: ['AI/ML', 'Advocacy', 'Research'],
  },
];

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function Portfolio() {
  const [showIntro, setShowIntro] = useState(true);
  const [activeSection, setActiveSection] = useState('');
  const [mousePos, setMousePos] = useState({ x: -300, y: -300 });

  useEffect(() => {
    const move = (e) => setMousePos({ x: e.clientX, y: e.clientY });
    window.addEventListener('mousemove', move);
    return () => window.removeEventListener('mousemove', move);
  }, []);

  useEffect(() => {
    const ids = ['about', 'experience', 'projects', 'involvement', 'contact'];
    const obs = ids.map((id) => {
      const el = document.getElementById(id);
      if (!el) return null;
      const o = new IntersectionObserver(
        ([e]) => { if (e.isIntersecting) setActiveSection(id); },
        { threshold: 0.35 }
      );
      o.observe(el);
      return o;
    });
    return () => obs.forEach((o) => o?.disconnect());
  }, []);

  // staggered hero item props
  const h = (i) => ({
    initial: { opacity: 0, y: 24 },
    animate: { opacity: 1, y: 0 },
    transition: { delay: 0.1 + i * 0.13, duration: 0.8, ease: [0.22, 1, 0.36, 1] },
  });

  const mono = { fontFamily: "'JetBrains Mono', monospace" };
  const heading = { fontFamily: "'Space Grotesk', sans-serif" };

  return (
    <>
      <AnimatePresence>
        {showIntro && <IntroScreen key="intro" onComplete={() => setShowIntro(false)} />}
      </AnimatePresence>

      <div className="min-h-screen bg-[#09090f] text-white overflow-x-hidden">

        {/* ── Background grid ── */}
        <div
          className="fixed inset-0 pointer-events-none"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.022) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.022) 1px, transparent 1px)',
            backgroundSize: '64px 64px',
          }}
        />
        {/* Mouse grid glow */}
        <div
          className="fixed inset-0 pointer-events-none z-[1]"
          style={{
            background: `radial-gradient(500px circle at ${mousePos.x}px ${mousePos.y}px, rgba(94,234,212,0.05) 0%, transparent 65%)`,
          }}
        />

        {/* ── NAV ── */}
        <header className="fixed top-0 left-0 right-0 z-40 px-6 py-5">
          <nav className="max-w-5xl mx-auto flex justify-between items-center">
            <span className="text-[10px] text-white/35 tracking-[0.2em] uppercase select-none" style={mono}>
              ~/shelly-normatov
            </span>
            <div className="hidden md:flex gap-0.5 bg-white/[0.025] backdrop-blur-xl px-2 py-1.5 border border-white/[0.06]" style={{ borderRadius: '2px' }}>
              {['About', 'Experience', 'Projects', 'Involvement', 'Contact'].map((item) => (
                <a
                  key={item}
                  href={`#${item.toLowerCase()}`}
                  className={`text-[11px] px-4 py-1.5 transition-all duration-200 ${
                    activeSection === item.toLowerCase()
                      ? 'text-teal-400 bg-teal-400/8'
                      : 'text-white/30 hover:text-white/65 hover:bg-white/[0.035]'
                  }`}
                  style={{ ...mono, borderRadius: '2px' }}
                >
                  {item.toLowerCase()}
                </a>
              ))}
            </div>
          </nav>
        </header>

        {/* ══════════════════════════════ HERO ══════════════════════════════ */}
        <section className="relative min-h-screen flex flex-col justify-center items-center px-6 text-center pt-20">

          {/* status badge */}
          <motion.div {...h(0)} className="mb-10">
            <div
              className="inline-flex items-center gap-2.5 text-[10px] text-teal-400/55 tracking-[0.18em] uppercase border border-teal-400/12 px-4 py-2 bg-teal-400/[0.025]"
              style={{ ...mono, borderRadius: '2px' }}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-teal-400 animate-pulse" />
              <span className="text-white/18 mr-0.5">status:</span>
              running on matcha
            </div>
          </motion.div>

          {/* main name */}
          <motion.h1
              className="text-[clamp(1.9rem,7.4vw,6.4rem)] leading-[1.08] tracking-[-0.05em] mb-5"
              style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 800 }}
              initial={{ opacity: 0, y: 40, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ delay: 0.18, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            >
              <span className="text-white/80" style={{ fontWeight: 500 }}>Hey, I'm </span>
              <span
                style={{
                  WebkitBackgroundClip: 'text',
                  backgroundClip: 'text',
                  color: 'transparent',
                  backgroundImage: 'linear-gradient(135deg, #5eead4 0%, #818cf8 55%, #c084fc 100%)',
                }}
              >
                Shelly
              </span>
          </motion.h1>

          {/* subtitle */}
          <motion.p {...h(2)} className="text-white/42 text-lg md:text-[1.2rem] max-w-lg mx-auto mb-3 leading-relaxed font-light">
            CS Student @ University of Guelph, crafting full-stack experiences
            that are as thoughtful as they are functional.
          </motion.p>

          {/* terminal line */}
          <motion.p {...h(3)} className="text-white/22 text-sm max-w-md mx-auto mb-12 leading-relaxed" style={mono}>
            <span className="text-teal-400/35">$</span>{' '}passionate about{' '}
            <span className="text-teal-400/60">frontend</span>,{' '}
            <span className="text-violet-400/60">backend</span>, and everything in between
          </motion.p>

          {/* CTA buttons */}
          <motion.div {...h(4)} className="flex flex-wrap justify-center gap-3">
            <a
              href="#projects"
              className="px-8 py-3 bg-white text-black text-sm font-semibold hover:bg-teal-300 transition-all duration-200 hover:scale-[1.02]"
              style={{ ...mono, borderRadius: '2px' }}
            >
              ./projects
            </a>
            <a
              href="#contact"
              className="px-8 py-3 border border-white/10 text-white/55 text-sm hover:border-white/28 hover:text-white transition-all duration-200"
              style={{ ...mono, borderRadius: '2px' }}
            >
              ./contact
            </a>
          </motion.div>

          {/* scroll cue */}
          <motion.div
            className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1.5 text-white/12 animate-bounce"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.8, duration: 0.6 }}
          >
            <span className="text-[8px] tracking-[0.4em] uppercase" style={mono}>scroll</span>
            <ChevronDown size={11} />
          </motion.div>
        </section>

        {/* ══════════════════════════════ ABOUT ══════════════════════════════ */}
        <section id="about" className="relative z-10 py-36 px-6">
          <div className="max-w-5xl mx-auto">
            <Reveal>
              <p className="text-[10px] text-teal-400/38 tracking-[0.22em] mb-6" style={mono}>{'// 01 — about'}</p>
              <div className="grid md:grid-cols-2 gap-20 items-center">
                <div>
                  <h2
                    className="text-4xl md:text-[3.4rem] font-black text-white mb-8 leading-[1.03] tracking-[-0.025em]"
                    style={heading}
                  >
                    Building things
                    <br />
                    that <span className="text-teal-400">matter</span>.
                  </h2>
                  <div className="space-y-5 text-white/42 leading-relaxed text-[14px]">
                    <p>
                      What started as casually dipping my toes into programming turned into a steady
                      dive into learning, building, and figuring things out with code.
                    </p>
                    <p>
                      When I'm not debugging, you'll find me hiking trails. Turns out both coding
                      and hiking involve figuring out the best path forward, just with different bugs.
                    </p>
                    <p>
                      Outside of school, I help drive SOCIS workshops, research mixers, and coding
                      events, empowering students to turn "just testing the waters" into real impact.
                    </p>
                  </div>
                </div>
                <div className="relative flex justify-center">
                  <div
                    className="absolute inset-0 blur-3xl"
                    style={{ background: 'radial-gradient(circle, rgba(94,234,212,0.06) 0%, transparent 70%)' }}
                  />
                  <PhotoCarousel />
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        {/* ══════════════════════════════ SKILLS MARQUEE ══════════════════════════════ */}
        <div className="py-5 overflow-hidden border-y border-white/[0.04] bg-white/[0.006]">
          <div
            className="skills-marquee flex gap-10 text-[10px] text-white/18 tracking-[0.2em] uppercase flex-shrink-0"
            style={{ whiteSpace: 'nowrap', ...mono }}
          >
            {[...skills, ...skills, ...skills].map((s, i) => (
              <span key={i} className="flex items-center gap-10 flex-shrink-0">
                {s}
                <span className="text-teal-400/22">✦</span>
              </span>
            ))}
          </div>
        </div>

        {/* ══════════════════════════════ EXPERIENCE ══════════════════════════════ */}
        <section id="experience" className="relative z-10 py-36 px-6">
          <div className="max-w-5xl mx-auto">
            <Reveal>
              <p className="text-[10px] text-teal-400/38 tracking-[0.22em] mb-6" style={mono}>{'// 02 — experience'}</p>
              <h2
                className="text-4xl md:text-[3.4rem] font-black text-white mb-20 leading-[1.03] tracking-[-0.025em]"
                style={heading}
              >
                Where I've <span className="text-violet-400">worked</span>.
              </h2>
            </Reveal>

            <div className="relative pl-7">
              <div className="absolute left-0 top-2 bottom-2 w-px bg-white/[0.05]" />

              {experiences.map((exp, i) => (
                <Reveal key={i} delay={i * 0.07} className="pb-14 relative">
                  <div
                    className="absolute -left-[29px] top-1.5 w-3.5 h-3.5 border border-teal-400/35 bg-[#09090f]"
                    style={{ borderRadius: '2px' }}
                  />
                  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-1.5">
                    <span className="text-[16px] font-bold text-white leading-tight">{exp.role}</span>
                    <span className="text-teal-400/55 text-[12px]" style={mono}>@ {exp.company}</span>
                  </div>
                  {exp.period && (
                    <p className="text-[10px] text-white/20 mb-5 tracking-wide" style={mono}>
                      {exp.period}{exp.location ? ` · ${exp.location}` : ''}
                    </p>
                  )}
                  <ul className="text-white/38 text-[13px] space-y-2.5 mb-5 leading-relaxed">
                    {exp.points.map((pt, j) => (
                      <li key={j} className="flex gap-3">
                        <span className="text-teal-400/25 mt-[5px] flex-shrink-0 text-xs" style={mono}>›</span>
                        {pt}
                      </li>
                    ))}
                  </ul>
                  <div className="flex flex-wrap gap-1.5">
                    {exp.tags.map((tag) => <Tag key={tag}>{tag}</Tag>)}
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* ══════════════════════════════ PROJECTS ══════════════════════════════ */}
        <section id="projects" className="relative z-10 py-36 px-6">
          <div className="max-w-5xl mx-auto">
            <Reveal>
              <p className="text-[10px] text-teal-400/38 tracking-[0.22em] mb-6" style={mono}>{'// 03 — projects'}</p>
              <h2
                className="text-4xl md:text-[3.4rem] font-black text-white mb-20 leading-[1.03] tracking-[-0.025em]"
                style={heading}
              >
                Things I've <span className="text-teal-400">built</span>.
              </h2>
            </Reveal>

            <div className="grid md:grid-cols-2 gap-3.5">
              {projects.map((p, i) => (
                <Reveal key={i} delay={i * 0.06}>
                  <Card className="h-full flex flex-col">
                    <ImageCarousel images={p.images} alt={p.title} />
                    <div className="flex-1 flex flex-col">
                      <div className="flex items-start justify-between mb-2.5">
                        <h3 className="text-white font-bold text-[15px] leading-tight" style={heading}>
                          {p.title}
                        </h3>
                        <a
                          href={p.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-white/18 hover:text-teal-400 transition-colors ml-3 mt-0.5 flex-shrink-0"
                        >
                          <ArrowUpRight size={15} />
                        </a>
                      </div>
                      <p className="text-white/35 text-[13px] leading-relaxed mb-5 flex-1">{p.desc}</p>
                      <div className="flex flex-wrap gap-1.5">
                        {p.tags.map((tag) => <Tag key={tag}>{tag}</Tag>)}
                      </div>
                    </div>
                  </Card>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* ══════════════════════════════ INVOLVEMENT ══════════════════════════════ */}
        <section id="involvement" className="relative z-10 py-36 px-6">
          <div className="max-w-5xl mx-auto">
            <Reveal>
              <p className="text-[10px] text-teal-400/38 tracking-[0.22em] mb-6" style={mono}>{'// 04 — involvement'}</p>
              <h2
                className="text-4xl md:text-[3.4rem] font-black text-white mb-20 leading-[1.03] tracking-[-0.025em]"
                style={heading}
              >
                Beyond the <span className="text-violet-400">code</span>.
              </h2>
            </Reveal>

            <div className="grid md:grid-cols-3 gap-3.5">
              {involvement.map((item, i) => (
                <Reveal key={i} delay={i * 0.07}>
                  <Card className="h-full flex flex-col">
                    <h3 className="text-white font-bold text-[14px] mb-1 leading-tight" style={heading}>
                      {item.title}
                    </h3>
                    <p className="text-[10px] text-white/22 mb-5 leading-snug" style={mono}>{item.subtitle}</p>
                    <ul className="text-white/35 text-[12.5px] space-y-2 mb-5 leading-relaxed flex-1">
                      {item.points.map((pt, j) => (
                        <li key={j} className="flex gap-2.5">
                          <span className="text-teal-400/22 mt-[4px] flex-shrink-0 text-xs" style={mono}>›</span>
                          {pt}
                        </li>
                      ))}
                    </ul>
                    <div className="flex flex-wrap gap-1.5 mt-auto">
                      {item.tags.map((tag) => <Tag key={tag}>{tag}</Tag>)}
                    </div>
                  </Card>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* ══════════════════════════════ CONTACT ══════════════════════════════ */}
        <section id="contact" className="relative z-10 py-36 px-6">
          <div className="max-w-3xl mx-auto text-center">
            <Reveal>
              <p className="text-[10px] text-teal-400/38 tracking-[0.22em] mb-5" style={mono}>{'// 05 — contact'}</p>
              <h2
                className="text-5xl md:text-[5.5rem] font-black text-white mb-6 leading-[0.91] tracking-[-0.035em]"
                style={heading}
              >
                Let's{' '}
                <span
                  style={{
                    ...heading,
                    WebkitBackgroundClip: 'text',
                    backgroundClip: 'text',
                    color: 'transparent',
                    backgroundImage: 'linear-gradient(135deg, #5eead4 0%, #818cf8 55%, #c084fc 100%)',
                  }}
                >
                  connect.
                </span>
              </h2>
              <p className="text-white/28 text-[13px] mb-14 max-w-sm mx-auto leading-relaxed" style={mono}>
                <span className="text-teal-400/38">$</span> open to new opportunities,
                cool projects, or just a good conversation.
              </p>
              <div className="flex flex-wrap justify-center gap-3">
                <a
                  href="mailto:shelly.normatov@gmail.com"
                  className="flex items-center gap-2 px-7 py-3 bg-white text-black text-sm font-semibold hover:bg-teal-300 transition-all duration-200 hover:scale-[1.02]"
                  style={{ ...mono, borderRadius: '2px' }}
                >
                  <Mail size={13} />
                  ./email
                </a>
                <a
                  href="https://www.linkedin.com/in/shellynormatov/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-7 py-3 border border-white/10 text-white/50 text-sm hover:border-white/28 hover:text-white transition-all duration-200"
                  style={{ ...mono, borderRadius: '2px' }}
                >
                  <Linkedin size={13} />
                  ./linkedin
                </a>
                <a
                  href="https://github.com/shell-mp3"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-7 py-3 border border-white/10 text-white/50 text-sm hover:border-white/28 hover:text-white transition-all duration-200"
                  style={{ ...mono, borderRadius: '2px' }}
                >
                  <Github size={13} />
                  ./github
                </a>
              </div>
            </Reveal>
          </div>
        </section>

        {/* ── FOOTER ── */}
        <footer className="relative z-10 py-6 px-6 border-t border-white/[0.04]">
          <div className="max-w-5xl mx-auto flex justify-between items-center text-[10px] text-white/14" style={mono}>
            <span>© 2025 shelly-normatov</span>
            <span>react + tailwind + framer-motion</span>
          </div>
        </footer>
      </div>
    </>
  );
}
